// ─────────────────────────────────────────────
//  УНФ-классификация номенклатуры (Блок 2, шаблон счёта для 1С:УНФ)
// ─────────────────────────────────────────────
//
// Два столбца Excel-шаблона счёта: «Категория» (поле УНФ «Классификация →
// Категория») и «Входит в группу» (группа товаров). Без них позиция, которой
// нет в справочнике УНФ, грузится как «Новая номенклатура» без классификации.
//
// Формат категории — ТОЧНАЯ строка УНФ, с кодом там, где он есть. Коды НЕ
// уникальны (5961 у Диодов/Тиристоров/Транзисторов, 5980 у Светодиодов/
// Индикаторов, 5945 у Реле/Переключателей) → ключ сопоставления — вся строка
// целиком, не код. Спецификация: docs/tech-debt.md, запись 2026-07-11.

/**
 * Словарь «имя категории без кода → точная строка УНФ (с кодом где есть)».
 * 24 категории с кодом + 6 без кода. Fallback «Прочие» — отдельной константой
 * UNF_CATEGORY_FALLBACK (в словаре не участвует: это не имя из УНФ-дерева,
 * а наш дефолт для нераспознанного).
 */
export const UNF_CATEGORY_BY_NAME: Record<string, string> = {
  // 24 с кодом
  Конденсаторы: 'Конденсаторы (5910)',
  Резисторы: 'Резисторы (5905)',
  Микросхемы: 'Микросхемы (5962)',
  'Модули; усилители': 'Модули; усилители (5963)',
  'Соединители электрические': 'Соединители электрические (5935)',
  'Диоды, Стабилитроны, Варикапы': 'Диоды, Стабилитроны, Варикапы (5961)',
  Тиристоры: 'Тиристоры (5961)',
  Транзисторы: 'Транзисторы (5961)',
  Трансформаторы: 'Трансформаторы (5950)',
  Реле: 'Реле (5945)',
  Переключатели: 'Переключатели (5945)',
  Ферриты: 'Ферриты (5999)',
  Светодиоды: 'Светодиоды (5980)',
  Индикаторы: 'Индикаторы (5980)',
  'Резонаторы, Генераторы, Фильтры': 'Резонаторы, Генераторы, Фильтры (5955)',
  Предохранители: 'Предохранители (5920)',
  'Источники питания, Преобразователи': 'Источники питания, Преобразователи (6130)',
  Батарейки: 'Батарейки (6135)',
  Аккумуляторы: 'Аккумуляторы (6140)',
  Электромоторы: 'Электромоторы (6105)',
  'Кабель электрический': 'Кабель электрический (6145)',
  'Кабель оптический': 'Кабель оптический (6015)',
  'Соединители оптические': 'Соединители оптические (6030)',
  'Платы печатные': 'Платы печатные (5998)',
  // 6 без кода — голым именем (написание УНФ as-is, включая регистр)
  'Датчик тока': 'Датчик тока',
  Модуль: 'Модуль',
  индуктивность: 'индуктивность',
  СТД: 'СТД',
  потенциометр: 'потенциометр',
  'Разъемы СПМ': 'Разъемы СПМ',
}

/** Fallback-категория для нераспознанных позиций. */
export const UNF_CATEGORY_FALLBACK = 'Прочие'

/** Все допустимые значения столбца «Категория» шаблона (31 = 24+6+fallback). */
export const UNF_CATEGORIES: readonly string[] = [
  ...Object.values(UNF_CATEGORY_BY_NAME),
  UNF_CATEGORY_FALLBACK,
]

// ─────────────────────────────────────────────
//  «Входит в группу» (группа товаров УНФ)
// ─────────────────────────────────────────────

/** Группа товаров УНФ: импортная / отечественная ЭКБ. */
export const UnfGroup = {
  IMPORT: 'ЭКБ ИП',
  DOMESTIC: 'ЭКБ ОП',
} as const
export type UnfGroup = (typeof UnfGroup)[keyof typeof UnfGroup]

const LATIN_RE = /[A-Za-z]/g
const CYRILLIC_RE = /[А-Яа-яЁё]/g

/**
 * Группа товаров по алфавиту партномера: большинство букв кириллица → «ЭКБ ОП»
 * (отечественная), иначе — «ЭКБ ИП» (импортная; в т.ч. без букв или при ничьей —
 * решение заказчика 13.07.2026: импорт — основной бизнес, спорное правится в УНФ).
 * Кириллица-двойники в латинском партномере схлопываются нормализацией НА
 * ВЫЗЫВАЮЩЕЙ СТОРОНЕ (normalizeArtKey в api) — сюда приходит строка as-is.
 */
export function resolveUnfGroup(partNumber: string | null | undefined): UnfGroup {
  if (!partNumber) return UnfGroup.IMPORT
  const latin = (partNumber.match(LATIN_RE) ?? []).length
  const cyrillic = (partNumber.match(CYRILLIC_RE) ?? []).length
  return cyrillic > latin ? UnfGroup.DOMESTIC : UnfGroup.IMPORT
}
